import {
  Bot,
  Edit,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api from "../http";
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useAssistantAvatar } from "../contexts/AssistantAvatarContext";
import { appConfig } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";

interface AnswerRevision {
  id: string;
  message_id: string;
  version: number;
  answer: string;
  old_answer?: string | null;
  created_at: string;
  edited_by_id: string | null;
  editor_email?: string | null;
}

interface MessageUser {
  id: string;
  type?: "user" | "contact";
  first_name: string | null;
  last_name: string | null;
  email?: string | null;
  profile_image_url?: string | null;
  phone_number?: string | null;
  country_code?: string | null;
  qr_code_id?: string | null;
}

interface Message {
  thread_id: number;
  id: number;
  user_id: string;
  openai_id: string;
  question: string;
  answer: string;
  email: string;
  assistant_id?: number | null;
  assistant_name?: string | null;
  assistant_photo_url?: string | null;
  user: MessageUser | null;
  feedback: "liked" | "disliked" | null;
  edited_by_email: string | null;
  answer_revisions?: AnswerRevision[];
  profile_image: string;
  admin_feedback: "liked" | "disliked" | null;
  admin_feedback_by_id?: string | null;
  admin_feedback_by_email?: string | null;
  admin_feedback_at?: string | null;
  comment: string | null;
  created_at: string;
  is_archived?: boolean | null;
  original_answer?: string | null;
  latest_admin_answer?: string | null;
  last_edited_at?: string | null;
}

interface Source {
  name: string;
  url: string;
}

type AdminFeedbackSelection = "liked" | "disliked" | "none";

// Function to extract sources from message content
const extractSources = (
  content: string | null | undefined,
): { cleanContent: string; sources: Source[] } => {
  try {
    const sources: Source[] = [];

    // Handle null/undefined content
    if (!content || typeof content !== "string") {
      return { cleanContent: content || "", sources };
    }

    // Pattern to match sources section and extract sources
    const sourcesRegex = /\*\*(?:Sources|Quellen)\*\*\s*([\s\S]*?)(?=\n\n|\n*$)/i;
    const sourcesMatch = content.match(sourcesRegex);

    if (sourcesMatch) {
      const sourcesSection = sourcesMatch[1];

      // Extract individual source links: [📄 filename](url)
      const sourceRegex = /\[📄\s*([^\]]+)\]\(([^)]+)\)/g;
      let sourceMatch;

      while ((sourceMatch = sourceRegex.exec(sourcesSection)) !== null) {
        const name = sourceMatch[1]?.trim();
        const url = sourceMatch[2]?.trim();

        // Only add valid sources
        if (name && url) {
          sources.push({ name, url });
        }
      }

      // Remove the entire sources section from content
      const cleanContent = content.replace(sourcesRegex, "").trim();
      return { cleanContent, sources };
    }

    return { cleanContent: content, sources };
  } catch (error) {
    console.error("Error extracting sources:", error);
    // Return original content if extraction fails
    return { cleanContent: content || "", sources: [] };
  }
};

// Component for displaying source buttons
const SourceButtons = ({ sources }: { sources: Source[] }) => {
  if (sources.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {sources.map((source, index) => (
        <Button
          key={index}
          variant="outline"
          size="sm"
          className="rounded-full text-xs px-3 py-1 h-7 bg-white hover:bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-800 transition-colors"
          onClick={() =>
            window.open(source.url, "_blank", "noopener,noreferrer")
          }
        >
          <span className="mr-1">📄</span>
          <span className="truncate max-w-[200px]">{source.name}</span>
          <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
        </Button>
      ))}
    </div>
  );
};

export default function Responses() {
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const { getAccessToken, user } = useAuth();
  const { assistants, isLoadingPhoto } = useAssistantAvatar();
  const [messages, setMessages] = useState<Message[]>([]);
  const [editedFilter, setEditedFilter] = useState<
    "all" | "edited" | "not_edited"
  >("all");
  const [userFeedbackFilter, setUserFeedbackFilter] = useState<
    "all" | "liked" | "disliked" | "none"
  >("all");
  const [adminFeedbackFilter, setAdminFeedbackFilter] = useState<
    "all" | "liked" | "disliked" | "none"
  >("all");
  const [commentFilter, setCommentFilter] = useState<
    "all" | "with_comments" | "without_comments"
  >("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [archivedFilter, setArchivedFilter] = useState<
    "all" | "archived" | "non_archived"
  >("all");
  const [qrCodeFilter, setQrCodeFilter] = useState<string>("all");
  const [assistantFilter, setAssistantFilter] = useState<string>("all");
  const [availableUsers, setAvailableUsers] = useState<MessageUser[]>([]);
  const [availableQRCodes, setAvailableQRCodes] = useState<
    { id: string; message_count: number }[]
  >([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingQRCodes, setIsLoadingQRCodes] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRevisions, setSelectedRevisions] = useState<AnswerRevision[]>(
    []
  );
  const [selectedRevisionAssistantName, setSelectedRevisionAssistantName] =
    useState(appConfig.assistantName);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const [previousPage, setPreviousPage] = useState(1);
  const [editingMessageId, setEditingMessageId] = useState<number>(0);
  const [editedAnswer, setEditedAnswer] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const isFetchingMessagesRef = useRef(false);
  const lastFiltersKeyRef = useRef("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [messageIdForCorrection, setMessageIdForCorrection] = useState<number | null>(null);
  const [correctionAnswer, setCorrectionAnswer] = useState<string>("");
  const [isSubmittingCorrection, setIsSubmittingCorrection] = useState(false);

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
    current_assistant_id?: number | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);




  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Load available users on component mount
  useEffect(() => {
    const loadAvailableUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const token = await getAccessToken();
        const response = await api.fetchMessageUsers(token);
        setAvailableUsers(response.users);
      } catch (error) {
        console.error("Failed to load users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadAvailableUsers();
  }, [getAccessToken]);

  useEffect(() => {
    const loadAvailableQRCodes = async () => {
      setIsLoadingQRCodes(true);
      try {
        const token = await getAccessToken();
        const response = await api.fetchQRCodes(token);
        setAvailableQRCodes(response.qr_codes);
      } catch (error) {
        console.error("Failed to load QR codes:", error);
      } finally {
        setIsLoadingQRCodes(false);
      }
    };

    loadAvailableQRCodes();
  }, [getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });

      // Also update the user profile image in messages if they exist there
      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.user?.id === currentUser.id
            ? {
                ...message,
                user: {
                  ...message.user,
                  profile_image_url: imageUrl,
                },
              }
            : message
        )
      );
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);

    // Also update the user information in messages if they exist there
    setMessages((prevMessages) =>
      prevMessages.map((message) =>
        message.user?.id === updatedUser.id
          ? {
              ...message,
              user: {
                id: message.user.id,
                email: message.user.email,
                first_name: updatedUser.first_name ?? null,
                last_name: updatedUser.last_name ?? null,
                profile_image_url: updatedUser.profile_image_url ?? null,
              } as MessageUser,
            }
          : message
      )
    );
  };

  const handleViewRevisions = (
    revisions: AnswerRevision[],
    assistantName: string
  ) => {
    setSelectedRevisions(revisions);
    setSelectedRevisionAssistantName(assistantName);
    setShowModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of the messages container
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
  };

  const getAdminHighlightClasses = (
    adminFeedback: "liked" | "disliked" | null | undefined
  ) => {
    if (adminFeedback === "liked") {
      return "bg-green-50 border border-green-200 shadow-sm";
    }
    if (adminFeedback === "disliked") {
      return "bg-red-50 border border-red-200 shadow-sm";
    }
    return "bg-blue-50 border border-blue-100 shadow-sm";
  };

  const fetchMessages = useCallback(
    async (
      userFeedback: string | null,
      adminFeedback: string | null,
      edited: string | null,
      comment: string | null,
      userId: string | null,
      archived: "all" | "archived" | "non_archived" = "all",
      qrCodeId: string | null = null,
      assistantId: number | null = null
    ) => {
      // Prevent concurrent calls using ref (synchronous check)
      if (isFetchingMessagesRef.current) {
        return;
      }

      isFetchingMessagesRef.current = true;

      try {
        const token = await getAccessToken();
        const fetchedMessages = await api.fetchMessages(
          token,
          currentPage,
          userFeedback === "all" ? null : userFeedback,
          adminFeedback === "all" ? null : adminFeedback,
          edited == "all" ? null : edited == "edited" ? true : false,
          comment == "all" ? null : comment == "with_comments" ? true : false,
          userId == "all" ? null : userId,
          archived,
          qrCodeId == "all" ? null : qrCodeId,
          assistantId
        );

        if (!fetchedMessages || !Array.isArray(fetchedMessages.messages)) {
          return;
        }

        // Apply sorting to the fetched messages
        const sortedMessages = [...fetchedMessages.messages].sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        });

        setTotalPages(fetchedMessages.totalPages);
        setNextPage(fetchedMessages.nextPage);
        setPreviousPage(fetchedMessages.previousPage);
        const filteredByAssistant =
          assistantId !== null
            ? sortedMessages.filter(
                (m) =>
                  typeof m.assistant_id === "number" &&
                  m.assistant_id === assistantId
              )
            : sortedMessages;

        setMessages(filteredByAssistant);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        isFetchingMessagesRef.current = false;
      }
    },
    [currentPage, getAccessToken, sortOrder]
  );

  const refreshMessages = useCallback(() => {
    const selectedAssistantId =
      assistantFilter === "all" ? null : Number(assistantFilter);

    return fetchMessages(
      userFeedbackFilter,
      adminFeedbackFilter,
      editedFilter,
      commentFilter,
      userFilter,
      archivedFilter,
      qrCodeFilter,
      selectedAssistantId !== null && Number.isFinite(selectedAssistantId)
        ? selectedAssistantId
        : null
    );
  }, [
    adminFeedbackFilter,
    archivedFilter,
    assistantFilter,
    commentFilter,
    editedFilter,
    fetchMessages,
    qrCodeFilter,
    userFeedbackFilter,
    userFilter,
  ]);

  const filtersKey = useMemo(
    () =>
      JSON.stringify([
        adminFeedbackFilter,
        archivedFilter,
        assistantFilter,
        commentFilter,
        editedFilter,
        qrCodeFilter,
        sortOrder,
        userFeedbackFilter,
        userFilter,
      ]),
    [
      adminFeedbackFilter,
      archivedFilter,
      assistantFilter,
      commentFilter,
      editedFilter,
      qrCodeFilter,
      sortOrder,
      userFeedbackFilter,
      userFilter,
    ],
  );

  const handleSubmit = async (
    id: number,
    newAnswer: string | undefined,
    adminFeedbackOverride?: AdminFeedbackSelection
  ) => {
    const token = await getAccessToken();
    try {
      await api.updateMessage(
        id,
        token,
        newAnswer,
        adminFeedbackOverride
      );
      toast.success("Answer updated successfully!", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch {
      toast.error("Failed to update the answer.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const handleFeedbackChange = (id: number, feedback: "liked" | "disliked") => {
    const currentFeedback =
      messages.find((m) => m.id === id)?.admin_feedback ?? null;
    const nextValue: AdminFeedbackSelection =
      currentFeedback === feedback ? "none" : feedback;

    // If clicking "disliked" and it's not already disliked, open correction modal
    if (feedback === "disliked" && currentFeedback !== "disliked") {
      const message = messages.find((m) => m.id === id);
      if (message) {
        // Close inline editing mode if it's open for this message
        if (editingMessageId === id) {
          setEditingMessageId(0);
          setEditedAnswer("");
        }
        setMessageIdForCorrection(id);
        setCorrectionAnswer(message.answer ?? "");
        setShowCorrectionModal(true);
      }
      return;
    }

    // For "liked" or toggling off "disliked", proceed with normal flow
    setMessages((prevMessages) =>
      prevMessages.map((m) =>
        m.id === id
          ? {
              ...m,
              admin_feedback: nextValue === "none" ? null : nextValue,
              admin_feedback_by_email:
                nextValue === "none" ? null : currentUser?.email ?? null,
              admin_feedback_at:
                nextValue === "none"
                  ? null
                  : new Date().toISOString(),
            }
          : m
      )
    );
    handleSubmit(id, undefined, nextValue);
  };

  const handleCorrectionSubmit = async () => {
    if (!messageIdForCorrection || !correctionAnswer.trim()) {
      return;
    }

    setIsSubmittingCorrection(true);
    const message = messages.find((m) => m.id === messageIdForCorrection);
    
    if (!message) {
      setIsSubmittingCorrection(false);
      return;
    }

    try {
      // Submit with the corrected answer and reset admin_feedback to neutral
      await handleSubmit(messageIdForCorrection, correctionAnswer.trim(), "none");
      
      // Update local state to show the corrected answer and remove red highlighting
      setMessages((prevMessages) =>
        prevMessages.map((m) =>
          m.id === messageIdForCorrection
            ? {
                ...m,
                answer: correctionAnswer.trim(),
                original_answer: m.original_answer ?? m.answer,
                latest_admin_answer: correctionAnswer.trim(),
                last_edited_at: new Date().toISOString(),
                edited_by_email: user?.email ?? m.edited_by_email ?? null,
                admin_feedback: null, // Reset to neutral after correction
                admin_feedback_by_email: null,
                admin_feedback_at: null,
              }
            : m
        )
      );

      // Close modal and reset state
      setShowCorrectionModal(false);
      setMessageIdForCorrection(null);
      setCorrectionAnswer("");
      
      toast.success("Korrektur gespeichert!", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch {
      toast.error("Fehler beim Speichern der Korrektur.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsSubmittingCorrection(false);
    }
  };

  const handleCorrectionCancel = () => {
    setShowCorrectionModal(false);
    setMessageIdForCorrection(null);
    setCorrectionAnswer("");
  };

  useEffect(() => {
    const filtersChanged = lastFiltersKeyRef.current !== filtersKey;
    lastFiltersKeyRef.current = filtersKey;

    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    refreshMessages();
  }, [
    adminFeedbackFilter,
    archivedFilter,
    assistantFilter,
    commentFilter,
    currentPage,
    editedFilter,
    filtersKey,
    refreshMessages,
    qrCodeFilter,
    sortOrder,
    userFeedbackFilter,
    userFilter,
  ]);

  // Auto-refresh every 30 seconds to catch feedback updates
  useEffect(() => {
    const interval = setInterval(() => {
      refreshMessages();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [refreshMessages]);

  // More frequent refresh when page is visible (every 10 seconds)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh immediately
        refreshMessages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshMessages]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshMessages();
      toast.success("Daten aktualisiert!", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch {
      toast.error("Failed to refresh data", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <span className="text-xl font-semibold">Generierte Antworten</span>
          </div>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Desktop Filter bar */}
        <div className="hidden lg:block p-4 border-b border-gray-200">
          {/* Filter Dropdowns */}
          <div className="flex items-center gap-3 mb-4">
            {/* Assistant Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Assistant
              </label>
              <Select value={assistantFilter} onValueChange={setAssistantFilter}>
                <SelectTrigger className="w-[200px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Assistants</SelectItem>
                  {assistants.map((assistant) => (
                    <SelectItem key={assistant.id} value={String(assistant.id)}>
                      {assistant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bearbeitung Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Bearbeitung</label>
              <Select value={editedFilter} onValueChange={(value) => setEditedFilter(value as "all" | "edited" | "not_edited")}>
                <SelectTrigger className="w-[160px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="edited">Bearbeitet</SelectItem>
                  <SelectItem value="not_edited">Nicht bearbeitet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Benutzer-Feedback Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Benutzer-Feedback
              </label>
              <Select
                value={userFeedbackFilter}
                onValueChange={(value) =>
                  setUserFeedbackFilter(
                    value as "all" | "liked" | "disliked" | "none"
                  )
                }
              >
                <SelectTrigger className="w-[160px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="liked">Gefällt mir</SelectItem>
                  <SelectItem value="disliked">Gefällt mir nicht</SelectItem>
                  <SelectItem value="none">Keine Bewertung</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Admin-Feedback Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Admin-Feedback
              </label>
              <Select
                value={adminFeedbackFilter}
                onValueChange={(value) =>
                  setAdminFeedbackFilter(
                    value as "all" | "liked" | "disliked" | "none"
                  )
                }
              >
                <SelectTrigger className="w-[160px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="liked">Gut</SelectItem>
                  <SelectItem value="disliked">Schlecht</SelectItem>
                  <SelectItem value="none">Nicht bewertet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Kommentare Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Kommentare</label>
              <Select value={commentFilter} onValueChange={(value) => setCommentFilter(value as "all" | "with_comments" | "without_comments")}>
                <SelectTrigger className="w-[160px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="with_comments">Mit Kommentaren</SelectItem>
                  <SelectItem value="without_comments">Ohne Kommentare</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Archivierung Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Archivierung</label>
              <Select value={archivedFilter} onValueChange={(value) => setArchivedFilter(value as "all" | "archived" | "non_archived")}>
                <SelectTrigger className="w-[160px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="archived">Archiviert</SelectItem>
                  <SelectItem value="non_archived">Nicht archiviert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Benutzer</label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[200px] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Benutzer</SelectItem>
                  {isLoadingUsers ? (
                    <SelectItem value="loading" disabled>Lade Benutzer...</SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name || user.last_name || user.email || "Unbekannter Benutzer"}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* QR Code Filter - Only show if there are QR codes available */}
            {availableQRCodes.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">QR Code</label>
                <Select value={qrCodeFilter} onValueChange={setQrCodeFilter}>
                  <SelectTrigger className="w-[200px] bg-[#F8FAFC]">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Roboter-IDs</SelectItem>
                    {isLoadingQRCodes ? (
                      <SelectItem value="loading" disabled>Lade QR Codes...</SelectItem>
                    ) : (
                      availableQRCodes.map((qrCode) => (
                        <SelectItem key={qrCode.id} value={qrCode.id}>
                          {qrCode.id} ({qrCode.message_count} Nachrichten)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Sort and Refresh Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as "asc" | "desc")}>
                <SelectTrigger 
                  className="min-w-[140px] [&:focus-visible]:ring-1 [&:focus-visible]:ring-ring" 
                  style={{
                    backgroundColor: `${APP_COLOR}15`,
                    borderColor: `${APP_COLOR}33`,
                    transition: 'border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${APP_COLOR}33`;
                  }}
                >
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Neueste zuerst</SelectItem>
                  <SelectItem value="asc">Älteste zuerst</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                className={isRefreshing ? "animate-spin" : ""}
              />
              {isRefreshing ? "Aktualisiere..." : "Aktualisieren"}
            </button>
          </div>
        </div>

        {/* Mobile Filter bar */}
        <div className="lg:hidden p-4 border-b border-gray-200">
          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 gap-3 mb-4">
            {/* Assistant Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Assistant
              </label>
              <Select value={assistantFilter} onValueChange={setAssistantFilter}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Assistants</SelectItem>
                  {assistants.map((assistant) => (
                    <SelectItem key={assistant.id} value={String(assistant.id)}>
                      {assistant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bearbeitung Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Bearbeitung</label>
              <Select value={editedFilter} onValueChange={(value) => setEditedFilter(value as "all" | "edited" | "not_edited")}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="edited">Bearbeitet</SelectItem>
                  <SelectItem value="not_edited">Nicht bearbeitet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Benutzer-Feedback Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Benutzer-Feedback
              </label>
              <Select
                value={userFeedbackFilter}
                onValueChange={(value) =>
                  setUserFeedbackFilter(
                    value as "all" | "liked" | "disliked" | "none"
                  )
                }
              >
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="liked">Gefällt mir</SelectItem>
                  <SelectItem value="disliked">Gefällt mir nicht</SelectItem>
                  <SelectItem value="none">Keine Bewertung</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Admin-Feedback Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Admin-Feedback
              </label>
              <Select
                value={adminFeedbackFilter}
                onValueChange={(value) =>
                  setAdminFeedbackFilter(
                    value as "all" | "liked" | "disliked" | "none"
                  )
                }
              >
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="liked">Gut</SelectItem>
                  <SelectItem value="disliked">Schlecht</SelectItem>
                  <SelectItem value="none">Nicht bewertet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Kommentare Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Kommentare</label>
              <Select value={commentFilter} onValueChange={(value) => setCommentFilter(value as "all" | "with_comments" | "without_comments")}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="with_comments">Mit Kommentaren</SelectItem>
                  <SelectItem value="without_comments">Ohne Kommentare</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Archivierung Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Archivierung</label>
              <Select value={archivedFilter} onValueChange={(value) => setArchivedFilter(value as "all" | "archived" | "non_archived")}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="archived">Archiviert</SelectItem>
                  <SelectItem value="non_archived">Nicht archiviert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Benutzer</label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Benutzer</SelectItem>
                  {isLoadingUsers ? (
                    <SelectItem value="loading" disabled>Lade Benutzer...</SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name || user.last_name || user.email || "Unbekannter Benutzer"}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* QR Code Filter - Only show if there are QR codes available */}
            {availableQRCodes.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">QR Code</label>
                <Select value={qrCodeFilter} onValueChange={setQrCodeFilter}>
                  <SelectTrigger className="w-full bg-[#F8FAFC]">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle QR Codes</SelectItem>
                    {isLoadingQRCodes ? (
                      <SelectItem value="loading" disabled>Lade QR Codes...</SelectItem>
                    ) : (
                      availableQRCodes.map((qrCode) => (
                        <SelectItem key={qrCode.id} value={qrCode.id}>
                          {qrCode.id} ({qrCode.message_count} Nachrichten)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Sort and Refresh Controls */}
          <div className="flex gap-2 mb-3">
            <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as "asc" | "desc")}>
              <SelectTrigger 
                className="flex-1 text-sm [&:focus-visible]:ring-1 [&:focus-visible]:ring-ring" 
                style={{
                  backgroundColor: `${APP_COLOR}15`,
                  borderColor: `${APP_COLOR}33`,
                  transition: 'border-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${APP_COLOR}33`;
                }}
              >
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Neueste zuerst</SelectItem>
                <SelectItem value="asc">Älteste zuerst</SelectItem>
              </SelectContent>
            </Select>

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex-shrink-0 p-2 hover:bg-slate-100 rounded-md bg-transparent border border-gray-200 focus:outline-none active:outline-none text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
              title={isRefreshing ? "Wird aktualisiert..." : "Aktualisieren"}
            >
              <RefreshCw
                size={16}
                className={`${
                  isRefreshing ? "animate-spin text-blue-500" : ""
                } transition-colors`}
              />
            </button>
          </div>

          {/* QR Code Filter - Mobile - Only show if there are QR codes available */}
          {availableQRCodes.length > 0 && (
            <div className="mb-3">
              <Select value={qrCodeFilter} onValueChange={setQrCodeFilter}>
                <SelectTrigger className="w-full bg-[#F8FAFC]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle QR Codes</SelectItem>
                  {isLoadingQRCodes ? (
                    <SelectItem value="loading" disabled>Lade QR Codes...</SelectItem>
                  ) : (
                    availableQRCodes.map((qrCode) => (
                      <SelectItem key={qrCode.id} value={qrCode.id}>
                        {qrCode.id} ({qrCode.message_count} Nachrichten)
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-auto" ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <p className="text-center text-gray-500 p-4">
              Keine Nachrichten entsprechen den aktuellen Filtern.
            </p>
          ) : (
            <div className="w-full px-2 sm:px-4 lg:px-[100px] py-4 lg:py-6 space-y-0">
              {messages.map((message) => {
                const isEditedMessage = Boolean(
                  message.original_answer &&
                    message.answer &&
                    message.original_answer !== message.answer,
                );
                const originalExtract = extractSources(
                  isEditedMessage ? message.original_answer : message.answer,
                );
                const editedExtract = extractSources(message.answer);
                const originalCleanContent = originalExtract.cleanContent;
                const editedCleanContent = editedExtract.cleanContent;
                const sourcesForDisplay =
                  editingMessageId === message.id
                    ? extractSources(editedAnswer).sources
                    : editedExtract.sources;
                const messageAssistant = assistants.find(
                  (assistant) => assistant.id === message.assistant_id,
                );
                const messageAssistantName =
                  message.assistant_name ||
                  messageAssistant?.name ||
                  appConfig.assistantName;
                const messageAssistantPhotoUrl =
                  message.assistant_photo_url ||
                  messageAssistant?.photo_url ||
                  undefined;

                return (
                  <div
                    key={message.id}
                    className="border-b border-gray-200 mx-[-8px] sm:mx-[-16px] lg:mx-[-100px]"
                  >
                  <div className="px-2 sm:px-4 lg:px-[100px]">
                    {/* User message */}
                    <div className="p-3 lg:p-6">
                      <div className="flex items-start gap-2 lg:gap-3">
                        <Avatar className="w-10 h-10 lg:w-14 lg:h-14 flex-shrink-0">
                          <AvatarImage
                            src={message.user?.profile_image_url || undefined}
                            alt="User avatar"
                          />
                          <AvatarFallback>
                            {message.user?.first_name && message.user?.last_name
                              ? `${message.user.first_name.charAt(
                                  0
                                )}${message.user.last_name.charAt(
                                  0
                                )}`.toUpperCase()
                              : message.user?.first_name
                                  ?.charAt(0)
                                  ?.toUpperCase() ||
                                message.user?.last_name
                                  ?.charAt(0)
                                  ?.toUpperCase() ||
                                (message.user?.type === "contact"
                                  ? message.user.phone_number
                                    ? message.user.phone_number
                                        .charAt(
                                          message.user.phone_number.length - 1
                                        )
                                        .toUpperCase()
                                    : "C"
                                  : message.email
                                  ? message.email.charAt(0).toUpperCase()
                                  : "?")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                            <span className="font-medium text-sm lg:text-base">
                              {message.user?.first_name &&
                              message.user?.last_name
                                ? `${message.user.first_name} ${message.user.last_name}`
                                : message.user?.first_name ||
                                  message.user?.last_name ||
                                  (message.user?.type === "contact"
                                    ? message.user.phone_number
                                    : message.email) ||
                                  (message.user?.type === "contact"
                                    ? "Unbekannter Kontakt"
                                    : "Unbekannter Benutzer")}
                            </span>
                            {message.user?.first_name && (
                              <span className="text-xs lg:text-sm text-gray-500">
                                (
                                {message.user.type === "contact"
                                  ? message.user.phone_number
                                  : message.user.email}
                                )
                              </span>
                            )}
                            {message.user?.type === "contact" && message.user?.qr_code_id && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                Robot ID: {message.user.qr_code_id}
                              </span>
                            )}
                            {message.feedback && (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  message.feedback === "liked"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {message.feedback === "liked"
                                  ? "👍 Gut"
                                  : "👎 Schlecht"}
                              </span>
                            )}
                            <span className="text-xs lg:text-sm text-gray-500">
                              {format(
                                new Date(message.created_at),
                                "d. MMMM yyyy HH:mm",
                                { locale: de }
                              )}
                            </span>
                          </div>
                          <div className="mt-1 bg-[#F8FAFC] text-slate-700 p-3 rounded-2xl">
                            <p className="text-sm lg:text-base font-normal leading-relaxed text-slate-600 whitespace-pre-wrap">
                              {message.question}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider between question and answer */}
                    <div className="px-3 lg:px-6">
                      <div className="border-b border-gray-200"></div>
                    </div>

                    {/* AI response */}
                    <div className="p-3 lg:p-6 pl-12 lg:pl-16 bg-transparent">
                      <div className="flex items-start gap-2 lg:gap-3">
                        <Avatar className="w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0">
                        {isLoadingPhoto ? (
                            <AvatarFallback className="animate-pulse">
                              <Bot className="h-4 w-4 lg:h-6 lg:w-6 text-gray-400" />
                            </AvatarFallback>
                          ) : (
                            <>
                              <AvatarImage
                                src={messageAssistantPhotoUrl}
                                alt={messageAssistantName}
                                className="object-cover scale-100"
                              />
                              <AvatarFallback>
                                <Bot className="h-4 w-4 lg:h-6 lg:w-6 text-red-500" />
                              </AvatarFallback>
                            </>
                          )}
                        </Avatar>
                        <div className="w-full min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <span className="font-medium text-sm lg:text-base">
                                {messageAssistantName}
                              </span>
                              <span className="text-xs lg:text-sm text-gray-500">
                                {format(
                                  new Date(message.created_at),
                                  "d. MMMM yyyy HH:mm",
                                  { locale: de }
                                )}
                              </span>
                              {message.edited_by_email && (
                                <div className="flex items-center gap-1 text-xs lg:text-sm text-gray-500">
                                  <Edit
                                    size={12}
                                    className="lg:w-3.5 lg:h-3.5"
                                  />
                                  <span>{message.edited_by_email}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 lg:gap-2">
                              {message.answer_revisions &&
                                message.answer_revisions.length > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 transition-colors"
                                    onClick={() =>
                                      handleViewRevisions(
                                        message.answer_revisions ?? [],
                                        messageAssistantName
                                      )
                                    }
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3 w-3 lg:h-4 lg:w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                    <span className="hidden sm:inline">
                                      Versionen
                                    </span>
                                  </Button>
                                )}
                              {editingMessageId === message.id ? (
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700"
                                    onClick={() => {
                                      setEditingMessageId(0);
                                      setEditedAnswer(message.answer ?? "");
                                    }}
                                  >
                                    <span>Abbrechen</span>
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 text-white"
                                    style={{
                                      backgroundColor: APP_COLOR,
                                      borderColor: APP_COLOR,
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        APP_COLOR;
                                      e.currentTarget.style.color = "white";
                                      e.currentTarget.style.borderColor =
                                        "#3b82f6";
                                      e.currentTarget.style.borderWidth = "1px";
                                      e.currentTarget.style.filter =
                                        "brightness(0.8)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        APP_COLOR;
                                      e.currentTarget.style.color = "white";
                                      e.currentTarget.style.borderColor =
                                        APP_COLOR;
                                      e.currentTarget.style.borderWidth = "1px";
                                      e.currentTarget.style.filter = "none";
                                    }}
                                  onClick={() => {
                                      setEditingMessageId(0);
                                      handleSubmit(message.id, editedAnswer, "none");
                                      setMessages((prevMessages) =>
                                        prevMessages.map((m) =>
                                          m.id === message.id
                                            ? {
                                                ...m,
                                                answer: editedAnswer,
                                                original_answer:
                                                  m.original_answer ?? m.answer,
                                                latest_admin_answer: editedAnswer,
                                                last_edited_at: new Date().toISOString(),
                                                edited_by_email:
                                                  user?.email ?? m.edited_by_email ?? null,
                                                admin_feedback: null,
                                              }
                                            : m
                                        )
                                      );
                                    }}
                                  >
                                    <span>Speichern</span>
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 transition-colors"
                                  onClick={() => {
                                    setEditingMessageId(message.id);
                                    setEditedAnswer(message.answer ?? "");
                                  }}
                                >
                                  <Edit
                                    size={12}
                                    className="lg:w-3.5 lg:h-3.5"
                                  />
                                  <span className="hidden sm:inline">
                                    Bearbeiten
                                  </span>
                                </Button>
                              )}
                            </div>
                          </div>
                          {editingMessageId === message.id ? (
                            <textarea
                              className="mt-1 w-full p-3 border border-gray-300 rounded-2xl bg-[#F8FAFC] text-slate-700 text-sm lg:text-base"
                              value={editedAnswer}
                              onChange={(e) => setEditedAnswer(e.target.value)}
                              id="answer"
                            />
                          ) : (
                            <div className="mt-1 space-y-2">
                              <div
                                className={`${getAdminHighlightClasses(
                                  message.admin_feedback ?? null
                                )} text-slate-700 p-3 rounded-2xl transition-colors`}
                              >
                                {isEditedMessage && (
                                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                    Originale Antwort
                                  </div>
                                )}
                                <div className="text-sm lg:text-base font-normal leading-relaxed text-slate-600 whitespace-pre-wrap">
                                  <Markdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      a: ({ href, children }) => (
                                        <a
                                          href={href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                          {children}
                                        </a>
                                      ),
                                    }}
                                  >
                                    {originalCleanContent}
                                  </Markdown>
                                </div>
                              </div>
                              {isEditedMessage && editedCleanContent && (
                                <div
                                  className={`p-3 rounded-2xl text-slate-700 transition-colors ${getAdminHighlightClasses(
                                    message.admin_feedback ?? null
                                  )}`}
                                >
                                  <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-2">
                                    Bearbeitete Antwort
                                  </div>
                                  <div className="text-sm lg:text-base font-normal leading-relaxed whitespace-pre-wrap">
                                    <Markdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        a: ({ href, children }) => (
                                          <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {children}
                                          </a>
                                        ),
                                      }}
                                    >
                                      {editedCleanContent}
                                    </Markdown>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Display user comment if exists */}
                          {message.comment && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-md border-l-4 border-blue-200">
                              <div className="text-xs text-blue-600 font-medium mb-1">
                                Benutzerkommentar:
                              </div>
                              <div className="text-xs lg:text-sm text-blue-800">
                                {message.comment}
                              </div>
                            </div>
                          )}

                          {/* Sources buttons for assistant messages */}
                          {sourcesForDisplay.length > 0 ? (
                            <SourceButtons sources={sourcesForDisplay} />
                          ) : null}

                          <div className="flex flex-col items-end gap-2 mt-4">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className={`flex items-center gap-1 text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 rounded-full transition-all duration-200 ${
                                  message.admin_feedback === "liked"
                                    ? "!bg-green-200 !text-green-900 !border-green-400 shadow-md hover:!bg-green-300 hover:!text-green-900"
                                    : "!bg-green-50 !text-green-600 !border-green-200 hover:!bg-green-100 hover:!text-green-700"
                                }`}
                                onClick={() =>
                                  handleFeedbackChange(message.id, "liked")
                                }
                              >
                                <ThumbsUp size={14} className="lg:w-4 lg:h-4" />
                                <span>Gut</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className={`flex items-center gap-1 text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 rounded-full transition-all duration-200 ${
                                  message.admin_feedback === "disliked"
                                    ? "!bg-red-200 !text-red-900 !border-red-400 shadow-md hover:!bg-red-300 hover:!text-red-900"
                                    : "!bg-red-50 !text-red-600 !border-red-200 hover:!bg-red-100 hover:!text-red-700"
                                }`}
                                onClick={() =>
                                  handleFeedbackChange(message.id, "disliked")
                                }
                              >
                                <ThumbsDown
                                  size={14}
                                  className="lg:w-4 lg:h-4"
                                />
                                <span>Schlecht</span>
                              </Button>
                            </div>
                            {message.admin_feedback && message.admin_feedback_by_email && (
                              <span className="text-xs text-gray-500">
                                Bewertet von {message.admin_feedback_by_email}
                                {message.admin_feedback_at && (
                                  <span className="ml-1">
                                    {new Date(message.admin_feedback_at).toLocaleDateString("de-DE", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Desktop Pagination */}
          <div className="hidden lg:flex items-center justify-between p-4 bg-gray-50">
            {/* Pagination controls */}
            <div className="flex items-center gap-5">
              {/* Previous button */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage > 1) {
                    handlePageChange(previousPage);
                  }
                }}
                disabled={currentPage === 1}
                className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
              >
                &lt; Zurück
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-3">
                {(() => {
                  // Logic to show only 5 page numbers with dots
                  const pages = [];
                  const totalPagesCount = totalPages;

                  if (totalPagesCount <= 5) {
                    // If 5 or fewer pages, show all
                    for (let i = 1; i <= totalPagesCount; i++) {
                      pages.push(
                        <span
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`cursor-pointer ${
                            i === currentPage
                              ? "w-8 h-8 flex items-center justify-center rounded-full"
                              : "text-gray-700 hover:text-gray-900"
                          }`}
                          style={
                            i === currentPage
                              ? {
                                  color: APP_COLOR,
                                  backgroundColor: `${APP_COLOR}20`,
                                }
                              : undefined
                          }
                        >
                          {i}
                        </span>
                      );
                    }
                  } else {
                    // Always show first page
                    pages.push(
                      <span
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className={`cursor-pointer ${
                          1 === currentPage
                            ? "w-8 h-8 flex items-center justify-center rounded-full"
                            : "text-gray-700 hover:text-gray-900"
                        }`}
                        style={
                          1 === currentPage
                            ? {
                                color: APP_COLOR,
                                backgroundColor: `${APP_COLOR}20`,
                              }
                            : undefined
                        }
                      >
                        1
                      </span>
                    );

                    // Calculate range around current page
                    let startPage = Math.max(2, currentPage - 1);
                    let endPage = Math.min(
                      totalPagesCount - 1,
                      currentPage + 1
                    );

                    // Adjust if at the beginning or end
                    if (currentPage <= 3) {
                      endPage = 4;
                    } else if (currentPage >= totalPagesCount - 2) {
                      startPage = totalPagesCount - 3;
                    }

                    // Add dots after first page if needed
                    if (startPage > 2) {
                      pages.push(
                        <span key="dots1" className="text-gray-500">
                          ...
                        </span>
                      );
                    }

                    // Add pages around current page
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <span
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`cursor-pointer ${
                            i === currentPage
                              ? "w-8 h-8 flex items-center justify-center rounded-full"
                              : "text-gray-700 hover:text-gray-900"
                          }`}
                          style={
                            i === currentPage
                              ? {
                                  color: APP_COLOR,
                                  backgroundColor: `${APP_COLOR}20`,
                                }
                              : undefined
                          }
                        >
                          {i}
                        </span>
                      );
                    }

                    // Add dots before last page if needed
                    if (endPage < totalPagesCount - 1) {
                      pages.push(
                        <span key="dots2" className="text-gray-500">
                          ...
                        </span>
                      );
                    }

                    // Always show last page
                    pages.push(
                      <span
                        key={totalPagesCount}
                        onClick={() => handlePageChange(totalPagesCount)}
                        className={`cursor-pointer ${
                          totalPagesCount === currentPage
                            ? "w-8 h-8 flex items-center justify-center rounded-full"
                            : "text-gray-700 hover:text-gray-900"
                        }`}
                        style={
                          totalPagesCount === currentPage
                            ? {
                                color: APP_COLOR,
                                backgroundColor: `${APP_COLOR}20`,
                              }
                            : undefined
                        }
                      >
                        {totalPagesCount}
                      </span>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Next button */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage < totalPages) {
                    handlePageChange(nextPage);
                  }
                }}
                disabled={currentPage === totalPages}
                className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
              >
                Weiter &gt;
              </button>
            </div>

            {/* Results count - aligned to right */}
            <div className="text-sm text-gray-500">
              Seite {currentPage} von {totalPages}
            </div>
          </div>

          {/* Mobile Pagination */}
          <div className="lg:hidden p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) {
                      handlePageChange(previousPage);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  &lt; Zurück
                </button>
                <span className="text-sm text-gray-500">
                  Seite {currentPage} von {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      handlePageChange(nextPage);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  Weiter &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Revisions */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-[90vw] lg:w-[80%] max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex-shrink-0 flex justify-between items-center px-4 lg:px-6 py-3 border-b border-gray-200 bg-white">
              <span className="text-lg lg:text-xl font-semibold text-gray-800">
                Versionen
              </span>
              <button
                className="text-gray-500 hover:text-gray-700 focus:outline-none p-1"
                onClick={() => setShowModal(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 lg:h-6 lg:w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 space-y-3 lg:space-y-4">
              {selectedRevisions.map((revision) => (
                <div
                  key={revision.id}
                  className="p-3 lg:p-4 bg-gray-50 border border-gray-200 rounded-lg shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs lg:text-sm text-gray-600">
                      <span className="font-medium">Version:</span>{" "}
                      {revision.version}
                    </p>
                  </div>
                  <div className="flex items-start gap-2 lg:gap-3 mt-2">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden flex-shrink-0 bg-indigo-100 flex items-center justify-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-indigo-600 lg:w-6 lg:h-6"
                      >
                        <path
                          d="M20 6L12 2L4 6M20 6L12 10M20 6V18L12 22M12 10L4 6M12 10V22M4 6V18L12 22"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="w-full min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                        <span className="font-medium text-sm lg:text-base text-indigo-600">
                          {selectedRevisionAssistantName}
                        </span>
                        <span className="text-xs lg:text-sm text-gray-500">
                          {format(
                            new Date(revision.created_at),
                            "d. MMMM yyyy HH:mm",
                            { locale: de }
                          )}
                        </span>
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg shadow-inner">
                        <p className="text-xs lg:text-sm text-gray-700">
                          {revision.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />

      {/* Correction Modal */}
      <Dialog open={showCorrectionModal} onOpenChange={(open) => {
        if (!open && !isSubmittingCorrection) {
          handleCorrectionCancel();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden">
          <DialogHeader className="relative">
            <button
              className="absolute right-0 top-0 text-gray-500 hover:text-gray-700 focus:outline-none p-1"
              onClick={handleCorrectionCancel}
              disabled={isSubmittingCorrection}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 lg:h-6 lg:w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <DialogTitle>Korrektur eingeben</DialogTitle>
            <DialogDescription>
              Bitte geben Sie die korrekte Antwort ein, damit die KI daraus lernen kann.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Answer Display */}
            {messageIdForCorrection && (() => {
              const currentMessage = messages.find((m) => m.id === messageIdForCorrection);
              const currentAnswer = currentMessage?.answer ?? "";
              return (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Aktuelle Antwort:
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 max-h-48 overflow-y-auto">
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {currentAnswer}
                    </Markdown>
                  </div>
                </div>
              );
            })()}

            {/* Correction Textarea */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Korrigierte Antwort:
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm min-h-[200px] resize-y"
                value={correctionAnswer}
                onChange={(e) => setCorrectionAnswer(e.target.value)}
                placeholder="Geben Sie hier die korrekte Antwort ein..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700"
              onClick={handleCorrectionCancel}
              disabled={isSubmittingCorrection}
            >
              Abbrechen
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 rounded-full text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 text-white"
              onClick={handleCorrectionSubmit}
              disabled={isSubmittingCorrection || !correctionAnswer.trim()}
              style={{
                backgroundColor: APP_COLOR,
                borderColor: APP_COLOR,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  APP_COLOR;
                e.currentTarget.style.color = "white";
                e.currentTarget.style.borderColor =
                  "#3b82f6";
                e.currentTarget.style.borderWidth = "1px";
                e.currentTarget.style.filter =
                  "brightness(0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  APP_COLOR;
                e.currentTarget.style.color = "white";
                e.currentTarget.style.borderColor =
                  APP_COLOR;
                e.currentTarget.style.borderWidth = "1px";
                e.currentTarget.style.filter = "none";
              }}
            >
              {isSubmittingCorrection ? "Speichern..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
